import { STORAGE_KEY } from "../constants/storageKeys";

export function defaultWordProgress() {
  return {
    hearts: 0,
    lastWrong: false,
    reviewDue: "",
    reviewLevel: 0,
    unclearCount: 0,
    unknownCount: 0,
    consecutiveUnknownInLearning: 0,
    spellingWrongCount: 0,
    totalWrongCount: 0,
    totalRightCount: 0,
    wrongTypes: [],
    reviewPlan: [],
  };
}

export function createMemoryCapsule(title, desc, quote) {
  return {
    id: Date.now() + Math.random(),
    date: new Date().toISOString().slice(0, 10),
    title,
    desc,
    quote,
    soundMemory: "（预留：未来接入录音/提示音资源）",
  };
}

export function getDefaultState() {
  return {
    app: {
      hasShownEntrance: false,
      weather: {
        loading: true,
        text: "天气加载中",
        icon: "⛅",
        temperature: "--",
        source: "",
      },
      currentDateText: "",
      currentWeekdayText: "",
    },

    user: {
      name: "Bonjour Learner",
      streak: 0,
      lastCheckIn: "",
      energy: 0,
      dailyPlanCount: 10,

      learnedWordIds: [],
      starredWordIds: [],
      notes: {},
      wordProgress: {},

      learnedHistory: [],
      reviewHistory: [],
      spellingHistory: [],

      memoryCapsules: [],
      currentBook: "基础法语词书",

      friends: [],
      studyGroupEnergy: 0,

      wrongWordBook: [],

      // ====================== 🔥 新增：皮肤系统字段 ======================
      shards: 0, // 碎片数量
      unlockedSkins: ["eiffel-tower"], // 已解锁的皮肤ID，默认解锁埃菲尔铁塔
      currentSkin: "eiffel-tower", // 当前使用的皮肤ID
      
      // ====================== 🔥 新增：地标解锁字段 ======================
      unlockedLandmarks: [1], // 已解锁的地标ID，默认解锁第1个（埃菲尔铁塔）
    },

    ui: {
      screen: "home",
      message: "",
      detailWordId: null,
      detailTab: "usage",
      showCheckinText: false,
      showLandmarkUnlock: null,
      meaningExampleIndex: null,
      tempNote: "",
    },

    learnSession: null,
    reviewSession: null,
    spellingSession: null,
  };
}

export function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultState();

    const parsed = JSON.parse(raw);
    const defaults = getDefaultState();

    return {
      ...defaults,
      ...parsed,
      app: {
        ...defaults.app,
        ...(parsed.app || {}),
        weather: {
          ...defaults.app.weather,
          ...(parsed.app?.weather || {}),
        },
      },
      user: {
        ...defaults.user,
        ...(parsed.user || {}),
        dailyPlanCount: Number(parsed.user?.dailyPlanCount || 10),
        learnedWordIds: parsed.user?.learnedWordIds || [],
        starredWordIds: parsed.user?.starredWordIds || [],
        notes: parsed.user?.notes || {},
        wordProgress: parsed.user?.wordProgress || {},
        learnedHistory: parsed.user?.learnedHistory || [],
        reviewHistory: parsed.user?.reviewHistory || [],
        spellingHistory: parsed.user?.spellingHistory || [],
        memoryCapsules: parsed.user?.memoryCapsules || [],
        friends: parsed.user?.friends || [],
        wrongWordBook: parsed.user?.wrongWordBook || [],
        
        // ====================== 🔥 新增：皮肤系统字段的加载兜底 ======================
        shards: parsed.user?.shards ?? 0,
        unlockedSkins: parsed.user?.unlockedSkins ?? ["eiffel-tower"],
        currentSkin: parsed.user?.currentSkin ?? "eiffel-tower",
        
        // ====================== 🔥 新增：地标解锁字段的加载兜底 ======================
        unlockedLandmarks: parsed.user?.unlockedLandmarks ?? [1],
      },
      ui: {
        ...defaults.ui,
        ...(parsed.ui || {}),
      },
      learnSession: parsed.learnSession || null,
      reviewSession: parsed.reviewSession || null,
      spellingSession: parsed.spellingSession || null,
    };
  } catch {
    return getDefaultState();
  }
}

export function saveState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function getWordProgress(state, wordId) {
  return state.user.wordProgress[wordId] || defaultWordProgress();
}

export function setWordProgress(state, wordId, nextProgress) {
  return {
    ...state,
    user: {
      ...state.user,
      wordProgress: {
        ...state.user.wordProgress,
        [wordId]: nextProgress,
      },
    },
  };
}

export function ensureStarred(state, wordId) {
  if (state.user.starredWordIds.includes(wordId)) return state;

  return {
    ...state,
    user: {
      ...state.user,
      starredWordIds: [...state.user.starredWordIds, wordId],
    },
  };
}

export function addLearnHistory(state, count = 1) {
  const today = new Date().toISOString().slice(0, 10);
  const history = [...state.user.learnedHistory];
  const existing = history.find((h) => h.date === today);

  if (existing) existing.count += count;
  else history.push({ date: today, count });

  return {
    ...state,
    user: {
      ...state.user,
      learnedHistory: history,
    },
  };
}

export function addReviewHistory(state, count = 1) {
  const today = new Date().toISOString().slice(0, 10);
  const history = [...state.user.reviewHistory];
  const existing = history.find((h) => h.date === today);

  if (existing) existing.count += count;
  else history.push({ date: today, count });

  return {
    ...state,
    user: {
      ...state.user,
      reviewHistory: history,
    },
  };
}

export function addSpellingHistory(state, count = 1) {
  const today = new Date().toISOString().slice(0, 10);
  const history = [...state.user.spellingHistory];
  const existing = history.find((h) => h.date === today);

  if (existing) existing.count += count;
  else history.push({ date: today, count });

  return {
    ...state,
    user: {
      ...state.user,
      spellingHistory: history,
    },
  };
}

export function markSpellingWrong(state, wordId) {
  const progress = getWordProgress(state, wordId);

  return setWordProgress(state, wordId, {
    ...progress,
    spellingWrongCount: (progress.spellingWrongCount || 0) + 1,
    totalWrongCount: (progress.totalWrongCount || 0) + 1,
    wrongTypes: [...(progress.wrongTypes || []), "拼写错误"],
  });
}